import React, { useState } from 'react';
import { Info } from 'lucide-react';
import GiftCardViewModal from './GiftCardViewModal';
import GiftCardClaimModal from './GiftCardClaimModal';
import styles from './GiftCard.module.css';

/**
 * GiftCard Component
 * Gift voucher card with split design - colorful left section and white right section
 * Based on reference image design
 */
const GiftCard = ({ card, onClaim, onActivate, index = 0 }) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  
  // Check for "Claimed" status (case-insensitive)
  const isClaimed = card.status === 'claimed' || card.status === 'Claimed';
  const isActivated = card.isActivated || card.status === 'activated' || card.status === 'Activated';
  
  // Get status values from card
  const requiresDeposit = card.requiresDeposit || false; // giftCard.depositStatus
  const hasWagerRequirement = card.wagerRequirement !== undefined && card.wagerRequirement > 0; // giftCard.wagerStatus
  const depositStatus = card.depositStatus; // deposit.status (from API)
  const wagerStatus = card.wagerStatus; // wager.status (from API)
  const allUserStatus = card.allUserStatus || false; // giftCard.allUserStatus
  const isActive = card.isActive || false; // giftCard.isActive
  
  // Calculate wager progress - use requiredWagerUSD if available
  const totalWagerRequired = card.requiredWagerUSD || card.totalWager || 0;
  const currentWager = card.currentWager || 0;
  const wagerProgress = hasWagerRequirement && totalWagerRequired > 0 
    ? Math.min((currentWager / totalWagerRequired) * 100, 100) 
    : 0;
  
  // Determine if claim button should be enabled based on conditions
  let canClaim = false;
  let buttonText = 'ACTIVATE';
  
  if (isClaimed) {
    // Already claimed
    canClaim = false;
    buttonText = 'CLAIMED';
  } else {
    // Check deposit/wager requirements first (these take priority)
    let depositWagerMet = false;
    
    // Condition 1: If both depositStatus and wagerStatus are true, check deposit.status and wager.status
    if (requiresDeposit && hasWagerRequirement) {
      // Both required - BOTH must be true to enable claim
      depositWagerMet = depositStatus === true && wagerStatus === true;
    }
    // Condition 3: If depositStatus false and wagerStatus true, check wager.status
    else if (!requiresDeposit && hasWagerRequirement) {
      depositWagerMet = wagerStatus === true;
    }
    // Condition 3: If depositStatus true and wagerStatus false, check deposit.status
    else if (requiresDeposit && !hasWagerRequirement) {
      depositWagerMet = depositStatus === true;
    }
    // Condition 2: If all false (no deposit, no wager), check if both statuses are false or undefined
    else if (!requiresDeposit && !hasWagerRequirement) {
      // If depositStatus and wagerStatus are both false/undefined, can claim
      depositWagerMet = (depositStatus === false || depositStatus === undefined) && 
                       (wagerStatus === false || wagerStatus === undefined);
    }
    
    // Now check activation status and condition 4
    if (!isActivated) {
      // Not activated yet
      // Condition 4: If allUserStatus and isActive are both true, enable claim even if not activated
      // BUT only if deposit/wager requirements are met (or don't exist)
      if (allUserStatus && isActive) {
        canClaim = depositWagerMet;
        buttonText = 'CLAIM';
      } else {
        canClaim = false;
        buttonText = 'ACTIVATE';
      }
    } else {
      // Card is activated
      buttonText = 'CLAIM';
      
      // Must have deposit/wager requirements met (if they exist)
      canClaim = depositWagerMet;
      
      // Condition 4: If allUserStatus and isActive are both true, can enable if no requirements
      // But if requirements exist, they must be met first
      if (allUserStatus && isActive && !requiresDeposit && !hasWagerRequirement) {
        canClaim = true;
      }
    }
  }

  const handleClaimClick = () => {
    if (isClaimed) return;
    
    // If allUserStatus and isActive are true, can claim even if not activated
    if (allUserStatus && isActive) {
      if (!canClaim) return;
      // Can claim - open claim modal
      setShowClaimModal(true);
      return;
    }
    
    if (!isActivated) {
      // Not activated - open activation modal
      if (onActivate) {
        onActivate();
      }
      return;
    }
    
    if (!canClaim) {
      // Activated but conditions not met
      return;
    }
    
    // Can claim - open claim modal
    setShowClaimModal(true);
  };

  const handleClaimSuccess = (cardId) => {
    // Close modal and trigger parent's onClaim
    setShowClaimModal(false);
    if (onClaim) {
      onClaim(cardId);
    }
  };

  const handleInfoClick = (e) => {
    e.stopPropagation();
    setShowInfoModal(true);
  };

  // Generate voucher code
  const voucherCode = `GFT${card.cardId.padStart(5, '0')}`;

  return (
    <>
      <div className={`${styles.giftCard} ${isClaimed ? styles.claimed : ''}`}>
        {/* Colorful Left Section (2/3 width) */}
        <div className={styles.cardLeftSection}>
          {/* Background gradient with polygon shapes and blue SVG */}
          <div className={styles.gradientBackground}>
            <svg className={styles.polygonShapes} viewBox="0 0 400 300" preserveAspectRatio="none">
              <defs>
                <radialGradient id={`gradient-${card.cardId}`} cx="30%" cy="70%" r="60%">
                  <stop offset="0%" stopColor="rgba(147, 51, 234, 0.5)" />
                  <stop offset="50%" stopColor="rgba(59, 130, 246, 0.4)" />
                  <stop offset="100%" stopColor="rgba(147, 51, 234, 0.2)" />
                </radialGradient>
                <pattern id={`stars-${card.cardId}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1.5" fill="rgba(255, 255, 255, 0.4)" />
                </pattern>
                {/* Blue SVG burst pattern */}
                <radialGradient id={`blueBurst-${card.cardId}`} cx="50%" cy="50%">
                  <stop offset="0%" stopColor="rgba(59, 130, 246, 0.6)" />
                  <stop offset="50%" stopColor="rgba(59, 130, 246, 0.3)" />
                  <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
                </radialGradient>
              </defs>
              <rect width="100%" height="100%" fill={`url(#gradient-${card.cardId})`} />
              <rect width="100%" height="100%" fill={`url(#stars-${card.cardId})`} />
              
              {/* Blue SVG burst effect in background */}
              <circle cx="50" cy="150" r="120" fill={`url(#blueBurst-${card.cardId})`} opacity="0.4" />
              <circle cx="300" cy="80" r="80" fill={`url(#blueBurst-${card.cardId})`} opacity="0.3" />
              
              {/* Radial lines emanating from multiple points */}
              <g opacity="0.25">
                {/* Lines from bottom-left */}
                {Array.from({ length: 8 }).map((_, i) => {
                  const angle = (i * 45) - 90;
                  const x1 = 0;
                  const y1 = 300;
                  const x2 = 400 + Math.cos((angle * Math.PI) / 180) * 200;
                  const y2 = 300 + Math.sin((angle * Math.PI) / 180) * 200;
                  return (
                    <line
                      key={`line1-${i}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="rgba(59, 130, 246, 0.4)"
                      strokeWidth="1.5"
                    />
                  );
                })}
                {/* Lines from center-left */}
                {Array.from({ length: 6 }).map((_, i) => {
                  const angle = (i * 60) - 30;
                  const x1 = 50;
                  const y1 = 150;
                  const x2 = 50 + Math.cos((angle * Math.PI) / 180) * 250;
                  const y2 = 150 + Math.sin((angle * Math.PI) / 180) * 250;
                  return (
                    <line
                      key={`line2-${i}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="rgba(147, 51, 234, 0.3)"
                      strokeWidth="1"
                    />
                  );
                })}
              </g>
              
              {/* Blue polygon shapes */}
              <polygon
                points="0,0 150,50 120,150 0,200"
                fill="rgba(59, 130, 246, 0.15)"
                opacity="0.6"
              />
              <polygon
                points="200,0 350,30 380,120 300,200 200,180"
                fill="rgba(59, 130, 246, 0.1)"
                opacity="0.5"
              />
            </svg>
          </div>

          {/* Title Section */}
          <div className={styles.titleSection}>
            <div className={styles.subtitle}>Your Gift Card</div>
            <div className={styles.mainTitle}>
              <span className={styles.titleGift}>GIFT</span>
              <span className={styles.titleVoucher}>VOUCHER</span>
            </div>
          </div>

          {/* Value Display with Radial Burst */}
          <div className={styles.valueSection}>
            <div className={styles.valueBurst}>
              <div className={styles.valueAmount}>${card.amount.toLocaleString('en-IN')}</div>
              {/* <div className={styles.valueLabel}>VALUE</div> */}
            </div>
          </div>

          {/* Claim/Activate Button - Bottom Left */}
          <button
            className={`${styles.claimButtonLeft} ${
              isClaimed 
                ? styles.claimButtonDisabled 
                : !isActivated 
                  ? styles.activateButtonStyle 
                  : !canClaim 
                    ? styles.claimButtonDisabled 
                    : styles.claimButtonStyle
            }`}
            onClick={handleClaimClick}
            disabled={isClaimed || !canClaim}
          >
            {buttonText}
          </button>

          {/* Bonus Icon in Middle */}
          <div className={styles.bonusIconContainer}>
            <img
              src="/assets/images/mobile-bonus.avif"
              alt="Bonus"
              className={styles.bonusIcon}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          {/* Gift Box with Springing Effect SVG */}
      
        </div>

        {/* White Right Section (1/3 width) */}
        <div className={styles.cardRightSection}>
          {/* Confetti Animation */}
          <div className={styles.confettiContainer}>
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className={styles.confetti}
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                  backgroundColor: ['#667eea', '#764ba2', '#48bb78', '#ed8936', '#e53e3e', '#fbbf24', '#3b82f6'][Math.floor(Math.random() * 7)]
                }}
              />
            ))}
          </div>
          
          {/* Header with Website URL and Info Button */}
          <div className={styles.rightSectionHeader}>
            <div className={styles.websiteUrl}>www.jackopot.com</div>
            <button
              className={styles.infoButton}
              onClick={handleInfoClick}
              aria-label="View card information"
            >
              <Info size={16} />
            </button>
          </div>

          {/* Voucher Code */}
          <div className={styles.voucherCodeSection}>
            <div className={styles.voucherCodeLabel}>Activation Code:</div>
            <div className={styles.voucherCode}>{card.activationCode || voucherCode}</div>
          </div>

          {/* Static Value - Always show the gift card amount */}
          {/* <div className={styles.staticValueSection}>
            <div className={styles.staticLabel}>STATIC VALUE:</div>
            <div className={styles.staticAmount}>${(card.staticValue || card.amount || 0).toLocaleString('en-IN')}</div>
          </div> */}

          {/* Status Badge - Full Width Button */}
          <div className={styles.statusBadge}>
            <span className={`${styles.badge} ${
              isClaimed 
                ? styles.badgeClaimed 
                : isActivated 
                  ? canClaim 
                    ? styles.badgeReadyToClaim 
                    : styles.badgeActivated 
                  : styles.badgeUnclaimed
            }`}>
              {isClaimed 
                ? 'CLAIMED' 
                : isActivated 
                  ? canClaim 
                    ? 'READY TO CLAIM' 
                    : 'ACTIVATED' 
                  : 'NOT ACTIVATED'}
            </span>
          </div>

          {/* Barcode */}
   
        </div>

        {/* Curved Divider */}
        <div className={styles.curvedDivider}>
          <svg viewBox="0 0 10 100" preserveAspectRatio="none">
            <path
              d="M 0 0 Q 5 50 0 100"
              fill="rgba(255, 255, 255, 0.1)"
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="0.5"
            />
          </svg>
        </div>
      </div>
      
      {/* View Modal */}
      <GiftCardViewModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        card={card}
        onActivate={onActivate}
      />

      {/* Claim Modal */}
      <GiftCardClaimModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        card={card}
        onClaim={handleClaimSuccess}
      />
    </>
  );
};

export default GiftCard;
